import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllContacts } from "@/services/firebase/user-functions";
import { ContactModel } from "common247";
import { ContactCard } from "@components/contacts/ContactCard";

const CONTACT_SORT_OPTIONS: ISortOption<ContactModel>[] = [
  { key: "displayName", label: "Display Name", getValue: (c) => c.displayName || "" },
  { key: "email", label: "Email", getValue: (c) => c.email || "" },
  { key: "owner", label: "Owner", getValue: (c) => c.ownerDisplayName || "" },
  { key: "createdAt", label: "Created Date", getValue: (c) => c.createdAt || 0 },
];

const CONTACT_GROUP_OPTIONS: IGroupOption<ContactModel>[] = [
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (c) => c.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "linkage",
    label: "Account Linkage",
    getGroupValue: (c) => (c.userId ? "Linked Registered Users" : "Unlinked Contacts"),
  },
  {
    key: "status",
    label: "Status",
    getGroupValue: (c) => (c.isActive !== false ? "Active Contacts" : "Inactive Contacts"),
  },
];

/**
 * Administrative screen displaying all registered contacts across users with view, sort, group, and filter capabilities.
 */
export default function ContactAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [contacts, setContacts] = useState<ContactModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAllContactsData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getAllContacts(services, {});
      if (response.status) {
        setContacts(response.contacts || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to load contacts.");
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching contacts.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchAllContactsData();
    }, [fetchAllContactsData]),
  );

  return (
    <ThemedScreen title="Contact Admin" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${contacts.length} TOTAL REGISTERED`}
        </ThemedText>

        <ThemedList<ContactModel>
          data={contacts}
          sortOptions={CONTACT_SORT_OPTIONS}
          defaultSortKey="displayName"
          defaultSortDirection="asc"
          groupOptions={CONTACT_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-contacts"
          pageSize={24}
          filterKeys={["displayName", "email", "ownerDisplayName"]}
          filterPlaceholder="Search all contacts..."
          loading={loading}
          onRefresh={() => fetchAllContactsData(true)}
          refreshing={refreshing}
          emptyText="No registered contacts found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => (
            <ContactCard
              contact={item}
              isAdminView={true}
              canEditTags={false}
              viewMode={viewMode}
              onInspect={() => CustomAlert.alert("Info", "Advanced user inspection coming soon.")}
            />
          )}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
    gap: 6,
  },
});
